import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  createApplication,
  getApplications,
  updateApplication,
  deleteApplication,
} from "../services/applicationService";
import { getCurrentUser, logout } from "../services/authService";
import { getResumes, createResume } from "../services/resumeService";
import { getCoverLetters, createCoverLetter, updateCoverLetter } from "../services/coverLetterService";
import ApplicationModal from "../components/applicationModal";
import AiFeedbackModal from "../components/AIFeedbackModal";
import ConfirmModal from "../components/confirmModal";
import ResumePickerModal from "../components/resumePickerModal";
import CoverLetterModal from "../components/coverLetterModal";
import { toast } from "../utils/toast";

const STATUS_COLORS = {
  Saved:         "#6b7280",
  Applied:       "#2563eb",
  "OA":          "#0891b2",
  Interview:     "#ff9d00",
  Accepted:      "#16a34a",
  Rejected:      "#ef4444",
};

const STATUS_COLORS_DARK = {
  Saved:         "#4b5563",
  Applied:       "#3b6bc9",
  "OA":          "#0e7490",
  Interview:     "#b97a00",
  Accepted:      "#15803d",
  Rejected:      "#b91c1c",
};

function useDarkMode() {
  const [dark, setDark] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

const HEAT_COLORS = ["#e5e7eb", "#bbf7d0", "#4ade80", "#16a34a", "#14532d"];

const localDate = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ActivityCalendarCustom({ data, range }) {
  const byDate = {};
  (data || []).forEach(({ date, count }) => { byDate[date] = count; });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = localDate(today);


  const days = [];
  const cursor = new Date(today);
  const lookback = range === "week" ? 6 : 29;
  cursor.setDate(cursor.getDate() - lookback);
  while (cursor <= today) { days.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }

  const weekDayHeaders = range === "week"
    ? days.map(d => DAY_HEADERS[d.getDay()])
    : DAY_HEADERS;

  const padStart = range === "week" ? [] : Array(days[0].getDay()).fill(null);

  return (
    <div className="activity-cal">
      <div className="activity-grid">
        {weekDayHeaders.map((d, i) => <div key={i} className="activity-day-header">{d}</div>)}
        {padStart.map((_, i) => <div key={`p${i}`} />)}
        {days.map(d => renderCell(d, byDate, today, todayStr))}
      </div>
    </div>
  );
}

function renderCell(d, byDate, today, todayStr) {
  const dateStr = localDate(d);
  const count = byDate[dateStr] || 0;
  const isFuture = d > today;
  const isToday = dateStr === todayStr;
  const level = count > 0 ? Math.min(count, HEAT_COLORS.length - 1) : 0;
  return (
    <div
      key={dateStr}
      className={`activity-cell activity-cell--level-${level}${isToday ? " activity-cell--today" : ""}${isFuture ? " activity-cell--future" : ""}`}
      title={count > 0 ? `${dateStr}: ${count} application${count > 1 ? "s" : ""}` : dateStr}
    >
      {count > 0 && <span className="activity-cell-count" style={{ color: level >= 3 ? "#fff" : "#14532d" }}>{count}</span>}
    </div>
  );
}

function renderLegend() {
  return (
    <div className="activity-legend">
      <span className="activity-legend-label">Less</span>
      {HEAT_COLORS.map((_, i) => (
        <div key={i} className={`activity-legend-cell activity-cell--level-${i}`} />
      ))}
      <span className="activity-legend-label">More</span>
    </div>
  );
}

export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get("demo") === "true";
  const navigate = useNavigate();
  const isDark = useDarkMode();
  const colors = isDark ? STATUS_COLORS_DARK : STATUS_COLORS;

  const [applications, setApplications] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [coverLetters, setCoverLetters] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [appModal, setAppModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statsTab, setStatsTab] = useState("activity");
  const [activityRange, setActivityRange] = useState("month");
  const [sortKey, setSortKey] = useState("submitted_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterStatus, setFilterStatus] = useState(null);
  const [dense, setDense] = useState(() => localStorage.getItem("dashDense") === "true");
  const [noSidebar, setNoSidebar] = useState(() => localStorage.getItem("dashNoSidebar") === "true");
  const [hideRejected, setHideRejected] = useState(false);
  const [hideInactive, setHideInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [activeApplication, setActiveApplication] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [resumeModal, setResumeModal] = useState(null);
  const [clModal, setClModal] = useState(null);
  const [docTargetApp, setDocTargetApp] = useState(null);
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStatusDropdownId(null);
      }
    };
    const handleScroll = () => setStatusDropdownId(null);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const [userData, resumeData, clData, appData] = await Promise.all([
          getCurrentUser(),
          getResumes(),
          getCoverLetters(),
          getApplications(),
        ]);
        setUser(userData);
        setResumes(resumeData || []);
        setCoverLetters(clData || []);
        setApplications(appData || []);
      } catch (err) {
        console.error("Failed to load dashboard data", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleAppSubmit = async (payload) => {
    try {
      if (appModal?.id) {
        const updated = await updateApplication(appModal.id, payload);
        setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      } else {
        const created = await createApplication(payload);
        setApplications((prev) => [created, ...prev]);
      }
    } catch (err) {
      console.error(err);
      toast("Failed to save application");
    }
  };

  const handleStatusChange = async (app, newStatus) => {
    setStatusDropdownId(null);
    try {
      const updated = await updateApplication(app.id, {
        title: app.title,
        job_id: app.job?.id,
        resume_id: app.resume_id || null,
        status: newStatus,
      });
      setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (err) {
      console.error(err);
      toast("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteApplication(id);
      setApplications((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error(err);
      toast("Failed to delete application");
    }
  };

  const handleClSubmit = async ({ title, language, application_id, file }) => {
    try {
      if (clModal?.id) {
        const updated = await updateCoverLetter(clModal.id, { title, language, application_id, file: file || undefined });
        setCoverLetters((prev) => prev.map((cl) => cl.id === updated.id ? updated : cl));
      } else {
        const created = await createCoverLetter({ title, language, application_id, file });
        setCoverLetters((prev) => [...prev, created]);
      }
    } catch (err) {
      console.error(err);
      toast("Failed to save cover letter");
    }
  };

  /* Stats */
  const total = applications.length;
  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});
  const uniqueCompanies = new Set(applications.map((a) => a.job?.company).filter(Boolean)).size;
  const withResume = applications.filter((a) => a.resume_id).length;
  const withCl = applications.filter((a) => coverLetters.some((cl) => cl.application_id === a.id)).length;
  const activeCount = applications.filter((a) => !["Rejected", "Saved", "Accepted"].includes(a.status)).length;
  const interviewRate = total > 0 ? Math.round(((statusCounts["Interview"] || 0) + (statusCounts["Accepted"] || 0)) / total * 100) : 0;
  const responseRate = total > 0 ? Math.round(((statusCounts["OA"] || 0) + (statusCounts["Interview"] || 0) + (statusCounts["Accepted"] || 0) + (statusCounts["Rejected"] || 0)) / total * 100) : 0;
  const appsWithDates = applications.filter((a) => a.submitted_at);
  const avgPerWeek = (() => {
    if (appsWithDates.length < 2) return appsWithDates.length;
    const dates = appsWithDates.map((a) => new Date(a.submitted_at));
    const earliest = Math.min(...dates);
    const latest = Math.max(...dates);
    const weeks = Math.max((latest - earliest) / (1000 * 60 * 60 * 24 * 7), 1);
    return (appsWithDates.length / weeks).toFixed(1);
  })();

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const companyCounts = applications.reduce((acc, a) => {
    const c = a.job?.company || "";
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});

  const filteredApplications = applications.filter((a) => {
    if (filterStatus && a.status !== filterStatus) return false;
    if (hideRejected && a.status === "Rejected") return false;
    if (hideInactive && ["Saved", "Rejected", "Accepted"].includes(a.status)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchCompany = a.job?.company?.toLowerCase().includes(q);
      const matchTitle = a.job?.title?.toLowerCase().includes(q);
      if (!matchCompany && !matchTitle) return false;
    }
    return true;
  });

  const sortedApplications = [...filteredApplications].sort((a, b) => {
    if (sortKey === "company") {
      const countDiff = (companyCounts[b.job?.company || ""] || 0) - (companyCounts[a.job?.company || ""] || 0);
      if (countDiff !== 0) return sortDir === "asc" ? -countDiff : countDiff;
      return (a.job?.company || "").localeCompare(b.job?.company || "");
    } else if (sortKey === "deadline") {
      const aVal = a.job?.deadline ? new Date(a.job.deadline) : null;
      const bVal = b.job?.deadline ? new Date(b.job.deadline) : null;
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    } else {
      const aVal = a.submitted_at ? new Date(a.submitted_at) : 0;
      const bVal = b.submitted_at ? new Date(b.submitted_at) : 0;
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    }
  });

  const heatmapData = (() => {
    const today = new Date();
    const todayStr = localDate(today);

    const start = new Date(today);
    if (activityRange === "week") start.setDate(start.getDate() - 7);
    else start.setMonth(start.getMonth() - 1);
    const startStr = localDate(start);

    const counts = {};
    applications.forEach((app) => {
      if (!app.submitted_at) return;
      const date = localDate(new Date(app.submitted_at));
      if (date >= startStr && date <= todayStr)
        counts[date] = (counts[date] || 0) + 1;
    });

    // Fill every day in range so calendar renders correctly
    const entries = [];
    const cursor = new Date(start);
    while (cursor <= today) {
      const d = localDate(cursor);
      const count = counts[d] || 0;
      entries.push({ date: d, count, level: Math.min(count, 4) });
      cursor.setDate(cursor.getDate() + 1);
    }
    return entries;
  })();

  const streak = (() => {
    const submittedDates = new Set(
      applications.filter(a => a.submitted_at).map(a => localDate(new Date(a.submitted_at)))
    );
    let count = 0;
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    while (submittedDates.has(localDate(cursor))) {
      count++;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  })();

  const upcomingDeadlines = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 7);
    return applications
      .filter(a => {
        if (!a.job?.deadline) return false;
        const d = new Date(a.job.deadline); d.setHours(0, 0, 0, 0);
        return d >= today && d <= cutoff;
      })
      .sort((a, b) => new Date(a.job.deadline) - new Date(b.job.deadline));
  })();

  return (
    <div className={`dash-layout${dense ? " dash-layout--dense" : ""}${noSidebar ? " dash-layout--no-sidebar" : ""}`}>
      {isDemo && (
        <div className="demo-banner">
          You're viewing a demo — data is temporary and will be deleted after 30 minutes.
          <button className="demo-banner-end" onClick={async () => { await logout(); navigate("/"); }}>End Demo</button>
        </div>
      )}

      {/* ---- MAIN GRID ---- */}
      <div className="app-page-grid">

        <div className="panel-card" style={{ minHeight: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="doc-col-header">
            <h2 className="app-header-title">
              Applications <span className="app-header-chip">{applications.length} total</span>
              <span className="app-header-chip">{activeCount} active</span>
              <span className="app-header-chip app-header-chip--streak">
                <span>{streak > 0 ? "★" : "☆"}</span>
                {streak} day streak
              </span>
            </h2>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className={`btn-ghost-sm${dense ? " active" : ""}`}
                title="Toggle compact rows"
                onClick={() => setDense(p => { localStorage.setItem("dashDense", !p); return !p; })}
              >≡</button>
              <button
                className={`btn-ghost-sm${noSidebar ? " active" : ""}`}
                title="Toggle analytics"
                onClick={() => setNoSidebar(p => { localStorage.setItem("dashNoSidebar", !p); return !p; })}
              >⊟</button>
              <button className="btn-primary-sm" onClick={() => setAppModal("create")}>+ Add</button>
            </div>
          </div>

          <div className="app-filter-bar">
            <input
              className="app-search-input"
              placeholder="Search company or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="app-filter-row">
              <div className="app-filter-pills">
                <button className={`app-filter-pill${!filterStatus ? " active" : ""}`} onClick={() => setFilterStatus(null)}>All</button>
                {Object.entries(colors).map(([s, color]) => (
                  <button
                    key={s}
                    className={`app-filter-pill${filterStatus === s ? " active" : ""}`}
                    style={filterStatus === s ? { backgroundColor: color, borderColor: color, color: "#fff" } : {}}
                    onClick={() => setFilterStatus(filterStatus === s ? null : s)}
                  >
                    <span className="status-dot" style={{ backgroundColor: filterStatus === s ? "#fff" : color }} />{s}
                  </button>
                ))}
              </div>
              <div className="app-filter-checks" style={{ marginLeft: "auto" }}>
                <label className="app-filter-check">
                  <input type="checkbox" checked={hideRejected} onChange={(e) => setHideRejected(e.target.checked)} />
                  Hide Rejected
                </label>
                <label className="app-filter-check">
                  <input type="checkbox" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} />
                  Active only
                </label>
              </div>
            </div>
          </div>

          <div className="cl-scrollable-list">
            {loading ? (
              <p className="doc-empty">Loading...</p>
            ) : applications.length === 0 ? (
              <p className="doc-empty">No applications yet</p>
            ) : (
              <div className="app-table">
                <div className="app-table-header">
                  <span></span>
                  <span className="sortable-col" onClick={() => handleSort("company")}>
                    Company {sortKey === "company" ? (sortDir === "asc" ? "↑" : "↓") : <span className="sort-hint">↕</span>}
                  </span>
                  <span>Role</span>
                  <span>Location</span>
                  <span className="sortable-col" onClick={() => handleSort("deadline")}>
                    Deadline {sortKey === "deadline" ? (sortDir === "asc" ? "↑" : "↓") : <span className="sort-hint">↕</span>}
                  </span>
                  <span className="sortable-col" onClick={() => handleSort("submitted_at")}>
                    Submitted {sortKey === "submitted_at" ? (sortDir === "asc" ? "↑" : "↓") : <span className="sort-hint">↕</span>}
                  </span>
                  <span>Status</span>
                  <span></span>
                </div>
                {sortedApplications.map((app, index) => {
                  const linkedCl = coverLetters.find((cl) => cl.application_id === app.id);
                  const isExpanded = expandedId === app.id;
                  const notes = app.job?.description;
                  const resume = resumes.find((r) => r.id === app.resume_id);
                  const deadline = app.job?.deadline
                    ? new Date(app.job.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : null;

                  return (
                    <div key={app.id}>
                    <div className="app-table-row-wrap">
                      <div
                        className="app-table-row"
                        style={{ borderLeftColor: colors[app.status] || "#9ca3af", cursor: "pointer" }}
                        onClick={() => setExpandedId(isExpanded ? null : app.id)}
                      >
                        <span className="app-table-cell app-table-cell--num">{index + 1}</span>
                        <span className="app-table-cell app-table-cell--title" title={app.job?.company || app.title || ""}>{app.job?.company || app.title || "—"}</span>
                        <span className="app-table-cell" title={app.job?.title || ""}>{app.job?.title || "—"}</span>
                        <span className="app-table-cell app-table-cell--muted" title={app.job?.location || ""}>{app.job?.location || "—"}</span>
                        <span className="app-table-cell app-table-cell--muted">{deadline || "—"}</span>
                        <span className="app-table-cell app-table-cell--muted">
                          {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </span>
                        <span className="app-table-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="status-dropdown-wrapper">
                            <span
                              className="status-badge status-badge--clickable"
                              style={{ backgroundColor: colors[app.status] || "#9ca3af" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (statusDropdownId === app.id) {
                                  setStatusDropdownId(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDropdownPos({ top: rect.bottom + 4, left: rect.left });
                                  setStatusDropdownId(app.id);
                                }
                              }}
                            >
                              {app.status} ▾
                            </span>
                            {statusDropdownId === app.id && (
                              <div
                                className="status-dropdown"
                                ref={dropdownRef}
                                style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left }}
                              >
                                {Object.entries(colors).map(([s, color]) => (
                                  <div
                                    key={s}
                                    className={`status-dropdown-item${app.status === s ? " active" : ""}`}
                                    onClick={() => handleStatusChange(app, s)}
                                  >
                                    <span className="status-dot" style={{ backgroundColor: color }} />
                                    {s}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </span>
                        <span className="app-table-cell app-table-cell--actions" onClick={(e) => e.stopPropagation()}>
                          <button className="btn-icon-edit" title="Edit" onClick={() => setAppModal(app)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon-delete" title="Delete" onClick={() => setConfirmDeleteId(app.id)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                          <button className="btn-ghost-sm" onClick={() => { setActiveApplication(app); setAiOpen(true); }}>AI Review</button>
                        </span>
                      </div>
                      {isExpanded && (
                        <div className="app-table-notes" style={{ borderLeftColor: colors[app.status] || "#9ca3af" }}>
                          <div className="app-doc-cards">
                            <div className="app-doc-card" onClick={(e) => { e.stopPropagation(); setDocTargetApp(app); setResumeModal(resume || "create"); }}>
                              <span className="app-doc-card-icon">📄</span>
                              <div className="app-doc-card-info">
                                <span className="app-doc-card-label">Resume</span>
                                <span className="app-doc-card-value">{resume ? resume.title : <span className="app-doc-card-empty">+ Add</span>}</span>
                              </div>
                            </div>
                            <div className="app-doc-card" onClick={(e) => { e.stopPropagation(); setDocTargetApp(app); setClModal(linkedCl || "create"); }}>
                              <span className="app-doc-card-icon">✉️</span>
                              <div className="app-doc-card-info">
                                <span className="app-doc-card-label">Cover Letter</span>
                                <span className="app-doc-card-value">{linkedCl ? linkedCl.title : <span className="app-doc-card-empty">+ Add</span>}</span>
                              </div>
                            </div>
                            <div className="app-doc-card" onClick={(e) => { e.stopPropagation(); setAppModal(app); }}>
                              <span className="app-doc-card-icon">💼</span>
                              <div className="app-doc-card-info">
                                <span className="app-doc-card-label">Type</span>
                                <span className="app-doc-card-value">{app.job?.employment_type || <span className="app-doc-card-empty">+ Add</span>}</span>
                              </div>
                            </div>
                            <div className="app-doc-card" onClick={(e) => { e.stopPropagation(); setAppModal(app); }}>
                              <span className="app-doc-card-icon">👤</span>
                              <div className="app-doc-card-info">
                                <span className="app-doc-card-label">Contact</span>
                                <span className="app-doc-card-value">{app.job?.contact_name || <span className="app-doc-card-empty">+ Add</span>}</span>
                              </div>
                            </div>
                            <div className="app-doc-card" onClick={(e) => { e.stopPropagation(); setAppModal(app); }}>
                              <span className="app-doc-card-icon">🔗</span>
                              <div className="app-doc-card-info">
                                <span className="app-doc-card-label">Job Posting</span>
                                <span className="app-doc-card-value">
                                  {app.job?.job_url
                                    ? <a href={app.job.job_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>View ↗</a>
                                    : <span className="app-doc-card-empty">+ Add</span>
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                          <span className="app-table-notes-label">Notes</span>
                          <p className="app-table-notes-text">{notes || "No notes"}</p>
                        </div>
                      )}
                    </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ---- STATS PANEL ---- */}
        <div className="app-stats-panel">
          <div className="panel-card" style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div className="stats-tabs">
              <button className={`stats-tab${statsTab === "activity" ? " active" : ""}`} onClick={() => setStatsTab("activity")}>Activity</button>
              <button className={`stats-tab${statsTab === "stats" ? " active" : ""}`} onClick={() => setStatsTab("stats")}>Analytics</button>
            </div>

            {statsTab === "stats" && (
              <div className="stats-tab-content">
                <div className="app-stats-heading">Overview</div>
                <div className="app-stats-row"><span>Response Rate</span><strong>{responseRate}%</strong></div>
                <div className="app-stats-row"><span>Interview Rate</span><strong>{interviewRate}%</strong></div>
                <div className="app-stats-row"><span>Avg / Week</span><strong>{avgPerWeek}</strong></div>
                <div className="app-stats-row"><span>Companies</span><strong>{uniqueCompanies}</strong></div>
                <div className="app-stats-row"><span>With Resume</span><strong>{withResume}</strong></div>
                <div className="app-stats-row"><span>With Cover Letter</span><strong>{withCl}</strong></div>

                <div className="app-stats-heading" style={{ marginTop: 16 }}>By Status</div>
                {Object.entries(colors).map(([status, color]) => (
                  <div key={status} className="app-stats-row">
                    <span className="app-stats-status-label">
                      <span className="status-dot" style={{ backgroundColor: color }} />
                      {status}
                    </span>
                    <strong>{statusCounts[status] || 0}</strong>
                  </div>
                ))}
              </div>
            )}

            {statsTab === "activity" && (
              <div className="activity-tab-wrapper">
                <div className="stats-tab-content">
                  <div className="app-stats-heading">Upcoming Deadlines</div>
                  {upcomingDeadlines.length === 0 ? (
                    <p className="activity-empty">No deadlines in the next 7 days</p>
                  ) : (
                    upcomingDeadlines.map(a => {
                      const [y, m, day] = a.job.deadline.slice(0, 10).split("-").map(Number);
                      const d = new Date(y, m - 1, day);
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const daysLeft = Math.round((d - today) / (1000 * 60 * 60 * 24));
                      const color = daysLeft <= 2
                                  ? { color: isDark ? "#fca5a5" : "#ef4444", background: isDark ? "#450a0a" : "#fef2f2" }
                                  : daysLeft <= 6
                                  ? { color: isDark ? "#fcd34d" : "#d97706", background: isDark ? "#422006" : "#fffbeb" }
                                  : {};
                      return (
                        <div key={a.id} className="activity-deadline-row">
                          <div className="activity-deadline-info">
                            <span className="activity-deadline-company">{a.job?.company}</span>
                            <span className="activity-deadline-role">{a.job?.title}</span>
                          </div>
                          <span className="activity-deadline-days" style={color}>
                            {daysLeft === 0 ? "Today" : daysLeft === 1 ? "Tomorrow" : `${daysLeft}d`}
                          </span>
                        </div>
                      );
                    })
                  )}

                  <div className="app-stats-heading" style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    Heatmap
                    <button
                      className="activity-range-toggle-btn"
                      onClick={() => setActivityRange(r => r === "week" ? "month" : "week")}
                    >
                      {activityRange === "week" ? "7d" : "30d"} <span className="activity-range-arrow">⇄</span>
                    </button>
                  </div>
                  <ActivityCalendarCustom data={heatmapData} range={activityRange} />
                </div>
                <div className="activity-legend-pinned">{renderLegend()}</div>
              </div>
            )}
          </div>
        </div>

      </div>

      {confirmDeleteId && (
        <ConfirmModal
          message="Are you sure you want to delete this application?"
          onConfirm={() => { handleDelete(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      {appModal && (
        <ApplicationModal
          onClose={() => setAppModal(null)}
          onSubmit={handleAppSubmit}
          resumes={resumes}
          currentUserId={user?.id}
          initialApp={appModal === "create" ? null : appModal}
        />
      )}

      {aiOpen && activeApplication && (
        <AiFeedbackModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          coverLetterText={coverLetters.find((cl) => cl.application_id === activeApplication.id)?.content || ""}
          resumeText={resumes.find((r) => r.id === activeApplication.resume_id)?.content || ""}
          jobText={activeApplication.job?.description || ""}
        />
      )}

      {resumeModal && (
        <ResumePickerModal
          onClose={() => { setResumeModal(null); setDocTargetApp(null); }}
          resumes={resumes}
          onPick={async (r) => {
            try {
              const updated = await updateApplication(docTargetApp.id, {
                title: docTargetApp.title,
                job_id: docTargetApp.job?.id,
                status: docTargetApp.status,
                resume_id: r.id,
              });
              setApplications((prev) => prev.map((a) => a.id === updated.id ? updated : a));
            } catch { toast("Failed to link resume"); }
            setResumeModal(null); setDocTargetApp(null);
          }}
          onCreateAndPick={async ({ title, file }) => {
            try {
              const created = await createResume({ title, file });
              setResumes((prev) => [...prev, created]);
              const updated = await updateApplication(docTargetApp.id, {
                title: docTargetApp.title,
                job_id: docTargetApp.job?.id,
                status: docTargetApp.status,
                resume_id: created.id,
              });
              setApplications((prev) => prev.map((a) => a.id === updated.id ? updated : a));
            } catch { toast("Failed to create resume"); }
            setResumeModal(null); setDocTargetApp(null);
          }}
        />
      )}

      {clModal && (
        <CoverLetterModal
          onClose={() => { setClModal(null); setDocTargetApp(null); }}
          onSubmit={handleClSubmit}
          initialCl={clModal === "create" ? null : clModal}
          applications={applications}
          preselectedAppId={docTargetApp?.id}
        />
      )}
    </div>
  );
}
